import { loadRecaptcha } from './recaptcha-loader.js';

export default class {
  constructor({
    id,
    useAjax,
    pageComponents,
    formHandle, // OPTIONAL: Pass explicitly for Craft forms, or let it auto-detect from 'data-handle'
  }) {
    this.el = document.getElementById(id);
    this.form = this.el.querySelector('form');
    this.useAjax = useAjax;

    this.formMessages = this.el.querySelector('.form__messages');

    this.pageComponents = pageComponents;

    if (!this.form) {
      return;
    }

    this.recaptcha = null;
    this.recaptchaVersion = null;
    this.recaptchaLoading = false; //Track loading state
    this.recaptchaReady = false; //Track ready state

    // Reference to the submit button and its copy element
    // Allows for updating the button during loading states
    this.submitBtn = this.form.querySelector('[type="submit"]');
    this.submitBtnCopy = this.submitBtn.querySelector('.btn-copy');

    if (this.submitBtnCopy) {
      this.submitBtn.dataset.label = this.submitBtnCopy.innerHTML;
    }

    this.handleSubmit = this.handleSubmit.bind(this);

    // Use explicit formHandle OR auto-detect from data-handle attribute
    this.formHandle = formHandle || this.detectFormHandle();

    // Add event handlers
    this.form.addEventListener('submit', this.handleSubmit);

    // Load reCAPTCHA and track loading state
    this.initRecaptcha();
  }

  //Initialize reCAPTCHA with proper loading state tracking
  initRecaptcha() {
    this.recaptchaLoading = true;
    this.recaptchaReady = false;

    loadRecaptcha(this.formHandle, this.form)
      .then(r => {
        this.recaptcha = r;
        this.recaptchaVersion = r.version;
        this.recaptchaLoading = false;
        this.recaptchaReady = true;

        // For v2, set up callback to clear errors when user completes reCAPTCHA
        if (this.recaptchaVersion === 'v2' && r.setCompletionCallback) {
          r.setCompletionCallback(() => {
            console.log('reCAPTCHA completed, clearing widget errors');
            this.clearRecaptchaWidgetErrors();
          });
        }

        console.log(`Loaded reCAPTCHA ${this.recaptchaVersion} for ${this.formHandle} form`);
      })
      .catch(error => {
        console.error('Failed to load reCAPTCHA:', error);
        this.recaptchaLoading = false;
        this.recaptchaReady = false;
      });
  }

  // Auto-detect form handle from data-handle attributes (for custom forms)
  detectFormHandle() {
    // Check for data-handle on the form element (like your locator_footer example)
    let handle = this.form.dataset.handle;
    if (handle) {
      console.log(`Auto-detected form handle: ${handle} from form data-handle`);
      return handle;
    }

    // Check for data-form-handle on the form element
    handle = this.form.dataset.formHandle;
    if (handle) {
      console.log(`Auto-detected form handle: ${handle} from form data-form-handle`);
      return handle;
    }

    // Check for data-handle on the container element
    handle = this.el.dataset.handle;
    if (handle) {
      console.log(`Auto-detected form handle: ${handle} from container data-handle`);
      return handle;
    }

    // Check for data-form-handle on the container element
    handle = this.el.dataset.formHandle;
    if (handle) {
      console.log(`Auto-detected form handle: ${handle} from container data-form-handle`);
      return handle;
    }

    // Default fallback
    console.log(`No form handle detected, using default: contact`);
    return 'contact';
  }

  // Handle form submission. Waits for reCAPTCHA to load if needed
  handleSubmit(event) {
    event.preventDefault();

    // If reCAPTCHA is still loading, show loading state and wait
    if (this.recaptchaLoading) {
      console.log('reCAPTCHA still loading, waiting...');
      this.toggleLoadingState(true, 'Loading security verification...');
      this.waitForRecaptchaAndSubmit();
      return;
    }

    // If reCAPTCHA failed to load, show error
    if (!this.recaptchaReady || this.recaptcha === null) {
      console.error('reCAPTCHA not available');
      this.showRecaptchaError('Security verification unavailable. Please refresh the page and try again.');
      return;
    }

    //For v2, check if user completed reCAPTCHA before proceeding
    if (this.recaptchaVersion === 'v2' && this.recaptcha.isCompleted && !this.recaptcha.isCompleted()) {
      this.showRecaptchaError('Please complete the reCAPTCHA verification by clicking the checkbox.');
      return;
    }

    this.proceedWithSubmission();
  }

  //Wait for reCAPTCHA to load, then submit
  waitForRecaptchaAndSubmit() {
    const checkInterval = 100; // Check every 100ms
    const maxWait = 10000; // Maximum 10 seconds
    let elapsed = 0;

    const checkRecaptcha = () => {
      elapsed += checkInterval;

      if (this.recaptchaReady && this.recaptcha) {
        console.log('reCAPTCHA loaded, proceeding with submission');
        this.proceedWithSubmission();
        return;
      }

      if (elapsed >= maxWait || (!this.recaptchaLoading && !this.recaptchaReady)) {
        console.error('reCAPTCHA loading timeout or failed');
        this.toggleLoadingState(false);
        this.showRecaptchaError('Security verification timed out. Please refresh the page and try again.');
        return;
      }

      setTimeout(checkRecaptcha, checkInterval);
    };

    checkRecaptcha();
  }

  // Proceed with actual form submission
  proceedWithSubmission() {
    // Clear any existing reCAPTCHA widget errors since we're proceeding
    this.clearRecaptchaWidgetErrors();

    this.toggleLoadingState(true);

    //Handle v2 and v3 differently
    if (this.recaptchaVersion === 'v2') {
      // For v2, we already checked if completed, so just get the token directly
      if (this.recaptcha.responseToken) {
        this.submit({
          recaptchaToken: this.recaptcha.responseToken,
          recaptchaVersion: this.recaptchaVersion
        });
      } else {
        // This shouldn't happen since we check in handleSubmit, but just in case
        this.toggleLoadingState(false);
        this.showRecaptchaError('Please complete the reCAPTCHA verification by clicking the checkbox.');
      }
    } else {
      // For v3, execute to get token
      this.recaptcha.execute('form')
        .then(token => {
          return this.submit({
            recaptchaToken: token,
            recaptchaVersion: this.recaptchaVersion
          });
        })
        .catch(error => {
          console.error('reCAPTCHA execution failed:', error);
          this.toggleLoadingState(false);
          this.showRecaptchaError('reCAPTCHA verification failed. Please try again.');
        });
    }

  }

  // Helper method to clear reCAPTCHA widget error messages
  clearRecaptchaWidgetErrors() {
    const recaptchaErrors = this.form.querySelectorAll('.recaptcha-error');
    recaptchaErrors.forEach(errorEl => {
      errorEl.style.display = 'none';
      errorEl.innerHTML = '';
    });
  }

  // Show reCAPTCHA specific error
  showRecaptchaError(message) {
    this.el.classList.add('form-error');
    this.el.classList.remove('form-success');

    // For v2 reCAPTCHA, try to show the error near the widget
    if (this.recaptchaVersion === 'v2') {
      const recaptchaWidget = this.form.querySelector('.recaptcha-container, .recaptcha-placeholder, [data-recaptcha-container], .g-recaptcha');
      if (recaptchaWidget) {
        this.showRecaptchaWidgetError(recaptchaWidget, message);
        return;
      }
    }

    // Fallback to general form messages
    if (this.formMessages) {
      const errorMessage = this.formMessages.querySelector('.error');
      if (errorMessage) {
        errorMessage.innerHTML = message;
        this.scrollToErrorMessage(errorMessage);
      }
    }
  }

  // Show error message attached to the reCAPTCHA widget
  showRecaptchaWidgetError(widgetElement, message) {
    // Look for existing error container near widget
    let errorContainer = widgetElement.querySelector('.recaptcha-error') ||
                        widgetElement.parentElement.querySelector('.recaptcha-error');

    // Create error container if it doesn't exist
    if (!errorContainer) {
      errorContainer = document.createElement('div');
      errorContainer.className = 'recaptcha-error';
      errorContainer.style.cssText = `
        color: rgb(238 0 0 / var(--tw-text-opacity));
        font-size: 14px;
        margin-top: 8px;
        margin-bottom: 22px;
        text-align: center;
        display: block;
      `;

      // Insert after the widget
      widgetElement.parentNode.insertBefore(errorContainer, widgetElement.nextSibling);
    }

    // Set the error message
    errorContainer.innerHTML = message;
    errorContainer.style.display = 'block';

    // Scroll to show both widget and error
    setTimeout(() => {
      // Scroll to the widget container which now includes the error
      const containerToScroll = widgetElement.parentElement.classList.contains('recaptcha-container')
        ? widgetElement.parentElement
        : widgetElement;

      containerToScroll.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }, 100);
  }

  // Helper method to scroll to general error messages
  scrollToErrorMessage(errorElement) {
    // Add a small delay to ensure DOM has updated
    setTimeout(() => {
      if (errorElement) {
        errorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 100);
  }

  // Single method for handling ajax and non-ajax
  // form submissions
  submit(params) {
    if (this.useAjax) {
      return this.submitWithAjax(params);
    }

    return this.submitWithoutAjax(params);
  }

  // Standard form submission. Append the recaptcha token input to the form and submit.
  submitWithoutAjax(params) {
    // Use appropriate field name based on version
    const fieldName = params.recaptchaVersion === 'v2' ? 'g-recaptcha-response' : 'token';

    let recaptchaTokenInput = this.form.querySelector(`input[name="${fieldName}"]`);
    if (!recaptchaTokenInput) {
      recaptchaTokenInput = document.createElement('input');
      recaptchaTokenInput.setAttribute('type', 'hidden');
      recaptchaTokenInput.setAttribute('name', fieldName);
      this.form.appendChild(recaptchaTokenInput);
    }

    recaptchaTokenInput.setAttribute('value', params.recaptchaToken);

    // Add version indicator for server
    let versionInput = this.form.querySelector('input[name="recaptcha_version"]');
    if (!versionInput) {
      versionInput = document.createElement('input');
      versionInput.setAttribute('type', 'hidden');
      versionInput.setAttribute('name', 'recaptcha_version');
      this.form.appendChild(versionInput);
    }
    versionInput.setAttribute('value', params.recaptchaVersion);

    this.form.submit();
    return Promise.resolve();
  }

  // Ajax submission. Get current session info and submit the request via ajax
  submitWithAjax(params) {
    const formData = new FormData(this.form);
    // Use appropriate field name based on version
    const fieldName = params.recaptchaVersion === 'v2' ? 'g-recaptcha-response' : 'token';
    formData.append(fieldName, params.recaptchaToken || '');
    formData.append('recaptcha_version', params.recaptchaVersion || 'v3');

    const actionField = this.form.querySelector('[name="action"]');
    let action = this.form.getAttribute('action');

    if (actionField) {
      action = actionField.value;
    }

    // For ajax requests, the form component needs to be able to update
    // the form inputs associated with the form. This finds those input components
    this.inputComponents = this.pageComponents ? this.pageComponents.findNestedComponents(this.form, 'input') : this.findInputComponentsFallback();

    return this.getSessionInfo()
      .then((sessionInfo) => {
        // Update the CSRF token with an updated one from the session info
        formData.set(sessionInfo.csrfTokenName, sessionInfo.csrfTokenValue);

        const request = {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        };

        const requestHandler = this.getRequestHandler();

        return requestHandler(request)
          .then((request) => {
            return fetch(action, request)
              .then((response) => {
                return response.json();
              })
              .then((response) => {
                return this.handleResponse(response);
              });
          });
      })
      .catch(() => {
      });
  }

  handleResponse(response) {
    this.toggleLoadingState(false);

    // Clear out any existing error messages
    (this.inputComponents || []).forEach((component) => {
      component.clearErrors();
    });

    const responseHandler = this.getResponseHandler();

    return responseHandler.call(this, response)
      .then((response) => {
        // Set the overall form state. Used to show the error/success state
        if (!response.success) {
          this.handleErrorResponse(response);
          return;
        }

        this.handleSuccessResponse(response);
      })
      .catch((result) => {
        console.warn(result);
      });
  }

  handleErrorResponse(response) {
    if (!this.formMessages) {
      return;
    }

    this.el.classList.add('form-error');
    this.el.classList.remove('form-success');

    // Clear any existing reCAPTCHA widget errors since these are form validation errors
    this.clearRecaptchaWidgetErrors();

    const errorMessage = this.formMessages.querySelector('.error');
    if (!errorMessage) {
      return;
    }

    let message = response.message;
    if (response.exception) {
      message = 'An error occurred during your submission.';
    }

    errorMessage.innerHTML = message;

    // Scroll to the error message
    this.scrollToErrorMessage(errorMessage);

    // Reset reCAPTCHA on error
    if (this.recaptcha && typeof this.recaptcha.reset === 'function') {
      this.recaptcha.reset();
    }

    // Update the error state for each field/input component that reported an error
    Object.keys(response.errors || {})
      .forEach((fieldName) => {
        // Check if this is a file field
        const isFileField = fieldName === 'contactFileUploads' || fieldName === 'contactNerdUpload' || fieldName === 'contactFormUploads';

        if (isFileField) {
          // Special handling for file fields
          const fileField = this.form.querySelector(`[name="${fieldName}"]`);
          if (fileField) {
            const fieldContainer = fileField.closest('.form-field');
            if (fieldContainer) {
              fieldContainer.classList.add('has-error');
              const errorEl = fieldContainer.querySelector('.error-message');
              if (errorEl) {
                errorEl.innerHTML = response.errors[fieldName].join('<br>');
              }
            }
          }
          return;
        }

        // Standard handling for other input types
        const inputComponent = (this.inputComponents || []).find((component) => {
          return component.input.name == fieldName;
        });

        if (!inputComponent) {
          return;
        }

        inputComponent.setErrors(response.errors[fieldName]);
      });
  }

  handleSuccessResponse(response) {
    if (response.redirect) {
      let redirect = response.redirect;

      // Ensure redirect is a string before using matches
      if (typeof redirect === 'string' && !redirect.match(/^http/)) {
        redirect = `/${redirect.replace(/^\/?/, '')}`;
      }

      window.location.href = redirect;
      return;
    }

    this.el.classList.remove('form-error');
    this.el.classList.add('form-success');

    if (!this.formMessages) {
      return;
    }

    const successMessage = this.formMessages.querySelector('.success');
    if (!successMessage) {
      return;
    }
    successMessage.scrollIntoView({ block: 'start' });
    // Use success state on the page
    // successMessage.innerHTML = response.message
  }

  //Support custom loading messages
  toggleLoadingState(show, customMessage = null) {
    if (show) {
      this.submitBtn.disabled = true;

      this.submitBtnCopy = this.submitBtn.querySelector('.btn-copy');
      if (this.submitBtnCopy) {
        this.submitBtnCopy.innerHTML = customMessage || 'Processing...';
      }

      return;
    }

    this.submitBtn.disabled = false;

    if (this.submitBtnCopy && this.submitBtn.dataset.label) {
      this.submitBtnCopy.innerHTML = this.submitBtn.dataset.label;
    }
  }

  // Helper for fetching a CSRF token:
  getSessionInfo() {
    return fetch('/actions/users/session-info', {
      headers: {
        'Accept': 'application/json',
      },
    })
      .then(response => response.json());
  }

  reset() {
    this.el.classList.remove('form-error');
    this.el.classList.remove('form-success');

    // Reset reCAPTCHA when form is reset
    if (this.recaptcha && typeof this.recaptcha.reset === 'function') {
      this.recaptcha.reset();
    }

    // Clear any reCAPTCHA widget errors
    this.clearRecaptchaWidgetErrors();

    (this.inputComponents || []).forEach((inputComponent) => {
      inputComponent.reset();
    });
  }

  setResponseHandler(responseHandler) {
    this.responseHandler = responseHandler;
  }

  getResponseHandler() {
    if (typeof this.responseHandler === 'function') {
      return this.responseHandler;
    }

    return (response) => {
      return Promise.resolve(response);
    };
  }

  setRequestHandler(requestHandler) {
    this.requestHandler = requestHandler;
  }

  getRequestHandler() {
    if (typeof this.requestHandler === 'function') {
      return this.requestHandler;
    }

    return (request) => {
      return Promise.resolve(request);
    };
  }

  // Fallback method to find input components when pageComponents is not available
  findInputComponentsFallback() {
    const inputComponents = [];
    const inputElements = this.form.querySelectorAll('[data-component]');

    inputElements.forEach(element => {
      try {
        const componentData = JSON.parse(element.getAttribute('data-component'));
        if (componentData.handle === 'input') {
          // Create a minimal input component interface
          const inputComponent = {
            input: element.querySelector('input, select, textarea'),
            clearErrors: () => {
              element.classList.remove('has-error');
              const errorMessage = element.querySelector('.error-message');
              if (errorMessage) {
                errorMessage.innerHTML = '';
              }
            },
            setErrors: (errors) => {
              if (errors.length > 0) {
                element.classList.add('has-error');
              }
              const errorMessage = element.querySelector('.error-message');
              if (errorMessage) {
                errorMessage.innerHTML = (errors || []).join(', ');
              }
            },
            reset: () => {
              element.classList.remove('has-error');
              const errorMessage = element.querySelector('.error-message');
              if (errorMessage) {
                errorMessage.innerHTML = '';
              }
            }
          };

          if (inputComponent.input) {
            inputComponents.push(inputComponent);
          }
        }
      } catch (e) {
        // Ignore invalid component data
      }
    });

    return inputComponents;
  }
}
